import { useEffect, useRef, useState } from 'react';

import { Logo } from '@/components/Logo';
import { Megamenu, MobileMenu } from '@/components/Megamenu';
import type { ContentfulMainHeaderFragment } from '@/graphql/contentful/api';
import { useScrollPosition } from '@/hooks/useScrollPosition';
import { useWindowWidth } from '@/hooks/useWindowWidth';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { theme } from '@/theme/getTheme';

import { CompoundProvider } from '../Header/context';
import type { MainHeaderProps } from '../Header/Header';
import { HeaderInfo } from './components/HeaderInfo';

import * as HeaderStyles from '../Header/Header.styles';
import * as S from './SimpleHeader.styles';

export const SimpleHeader = (mainHeader: MainHeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [isMobileHeader, setIsMobileHeader] = useState<boolean>(false);

  const scrollPosition = useScrollPosition();
  const resize = useWindowWidth();
  const { setHeaderRef } = useConfigProvider();
  const wrapperRef = useRef<HTMLElement>(null);

  const wrapperHeight = wrapperRef.current?.clientHeight ?? 0;
  const hideDesktopNav = wrapperRef.current
    ? scrollPosition > wrapperHeight
    : false;

  const headerBackground =
    mainHeader.background === theme.components.header.defaultBackground
      ? 'default'
      : 'white';

  const handleMenuState = (isOpen: boolean) => {
    setIsMobileMenuOpen(isOpen);
  };

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.removeProperty('overflow');
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if ((resize ?? window.innerWidth) <= 992) setIsMobileHeader(true);
    else setIsMobileHeader(false);
  }, [resize]);

  useEffect(() => {
    if (wrapperRef.current) {
      setHeaderRef(wrapperRef);
    }
    if (!isMobileHeader && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
      handleMenuState(false);
    }
  }, [isMobileHeader]);

  const getTextColor = () => {
    return mainHeader.textColorOption ?? 'light';
  };

  return (
    <CompoundProvider value={{ hideDesktopNav }}>
      <S.Wrapper
        ref={wrapperRef}
        $background={mainHeader.background}
        $textColorOption={getTextColor()}
        data-test-id='HeaderWrapper'
        data-testid='HeaderWrapper'
      >
        <HeaderStyles.HeaderBox data-test-id='HeaderBox'>
          <S.InnerWrapper data-test-id='HeaderInnerWrapper'>
            <HeaderStyles.ItemBox $logoOnly={mainHeader.logoOnly}>
              <HeaderStyles.LogoBox
                $headerType={mainHeader.type ?? 'Default'}
                $logoOnly={mainHeader.logoOnly ?? false}
                data-test-id='HeaderLogoBox'
              >
                <Logo data-test-id='HeaderLogo' isHeader={true} />
              </HeaderStyles.LogoBox>
              {!mainHeader.logoOnly && (
                <HeaderStyles.HeaderIconBox>
                  <HeaderInfo
                    data-test-id='HeaderInfo'
                    headerBackground={headerBackground}
                    hideLanguageSwitcher={true}
                    hidePhone={true}
                    hideSearch={true}
                    isMobileMenuOpen={isMobileMenuOpen}
                    isOpenMobile={isMobileMenuOpen}
                    isShrunk={false}
                    onMobileMenuHandler={handleMenuState}
                    onShoppingBagDrawerHandler={() => undefined}
                    textColorOption={getTextColor()}
                  />
                </HeaderStyles.HeaderIconBox>
              )}
            </HeaderStyles.ItemBox>
            <HeaderStyles.MegamenuBox
              $isDisplayed={!mainHeader.logoOnly}
              data-test-id='HeaderMegamenu'
            >
              <Megamenu
                headerBackground={headerBackground}
                hideDecoration={true}
                isDisplayed={!mainHeader.logoOnly}
                isShrunk={false}
                navItems={mainHeader as ContentfulMainHeaderFragment}
                textColorOption={getTextColor()}
              />
            </HeaderStyles.MegamenuBox>
          </S.InnerWrapper>
        </HeaderStyles.HeaderBox>
      </S.Wrapper>
      <MobileMenu
        data-test-id='HeaderMobileMenu'
        headerBackground={headerBackground}
        hideLanguageSwitcher={true}
        hidePhone={true}
        hideSearch={true}
        isMobileMenuOpen={isMobileMenuOpen}
        isOpen={isMobileMenuOpen}
        items={mainHeader as ContentfulMainHeaderFragment}
        onClose={handleMenuState}
        setShoppingBagDrawerOpen={() => undefined}
        shoppingBagDrawerOpen={false}
      />
    </CompoundProvider>
  );
};
